import type {
  BuildingType,
  BuildingView,
  CodexEntryView,
  CodexFamilyView,
  CulturalReviewStatus,
  DecodeMoveView,
  DecodeRunView,
  DecodeTutorialStepView,
  DiscoveryMarkerView,
  EnergyLinkView,
  KnowledgeWeaveView,
  MissionStepView,
  PlayerVerticalSliceView,
  ResourceCode,
  ResourceDisplay,
  SettlementDistrictView,
  SettlementHistoryEventView,
  SoloProgressionView,
  TribeFeedEventView,
  TribeRole,
  TribeSummaryView,
  WorldCapabilityView,
  WorldRouteContractView,
} from '@aztec-realms/shared-types';
import type { ApiComponents } from '@aztec-realms/api-client';
import { appVersion } from '../app-build';
import { plannedBuildingCapabilities } from '../settlement-goals';

import {
  browserApiClient as apiClient,
  type BrowserApiClient as ApiClient,
  requiredApiData as required,
} from './browser-api';
import { resolveAuthoritativeCurrentTribe, TRIBE_PRESENTATION_HINT_KEY } from './tribe-membership';
import type { VerticalSliceCommand } from './vertical-slice';

const DECODE_SESSION_KEY = 'aztec-realms:decode-session-id';
const DECODE_SELECTED_SESSION_KEY = 'aztec-realms:decode-selected-session-id-v1';
const DECODE_DISPLAYED_SESSION_KEY = 'aztec-realms:decode-displayed-session-id-v1';
const DECODE_CIVIC_SESSION_KEY = 'aztec-realms:decode-civic-session-id-v1';
const DECODE_PERSONAL_RETURN_KEY = 'aztec-realms:decode-personal-return-id-v1';
const JOURNEY_SESSION_KEY = 'aztec-realms:journey-session-id';
const VIEWPORT_BBOX = '4.84,52.33,4.95,52.42';
const CLIENT_VERSION = appVersion;

const resourceLabels: Readonly<Record<ResourceCode, string>> = {
  maize: 'Maize',
  stone: 'Stone',
  timber: 'Timber',
  jade: 'Jade',
  knowledge: 'Knowledge',
};

const branchPresentation: Readonly<
  Record<string, readonly { code: string; label: string; description: string }[]>
> = {
  market: [
    { code: 'caravan', label: 'Caravan', description: 'Awnings and wayfinding props.' },
    { code: 'artisan', label: 'Artisan', description: 'Craft stalls and workshop details.' },
    { code: 'civic', label: 'Civic', description: 'Plaza banners and public gardens.' },
  ],
  solar_shrine: [
    { code: 'waterworks', label: 'Waterworks', description: 'Canals and reflective terraces.' },
    { code: 'archive', label: 'Archive', description: 'Jade inlay and knowledge courts.' },
    { code: 'garden', label: 'Garden', description: 'Flower terraces and civic shade.' },
  ],
};

type SettlementResponse = ApiComponents['schemas']['SettlementResponse'];

function presentationLabel(code: string): string {
  return code
    .split('_')
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function mapSettlementDistrict(
  district: SettlementResponse['districts'][number],
): SettlementDistrictView {
  return {
    code: district.code,
    displayName: district.display_name,
    unlockOrder: district.unlock_order,
    bounds: {
      minX: district.min_x,
      maxX: district.max_x,
      minY: district.min_y,
      maxY: district.max_y,
    },
    unlocked: district.unlocked_at !== null,
    unlockedAt: district.unlocked_at ?? undefined,
    milestoneSource: district.milestone_source ?? undefined,
    unlockRequirements: district.unlock_requirements,
  };
}

export function mapSettlementHistoryEvent(
  event: SettlementResponse['history'][number],
): SettlementHistoryEventView {
  return {
    id: event.id,
    eventType: event.event_type,
    occurredAt: event.occurred_at,
    districtCode: event.district_code,
    buildingId: event.building_id ?? undefined,
    buildingCode: event.building_code ?? undefined,
    specializationCode: event.specialization_code ?? undefined,
    skinCode: event.skin_code ?? undefined,
    milestoneSource: event.milestone_source,
  };
}

export function mapWorldCapability(
  capability: SettlementResponse['world_capabilities'][number],
): WorldCapabilityView {
  return {
    code: capability.code,
    label: presentationLabel(capability.code),
    effectiveLevel: capability.effective_level,
    sourceBuildingId: capability.source_building_id,
    sourceBuildingCode: capability.source_building_code,
    specializationCode: capability.specialization_code ?? undefined,
    parameters: capability.parameters,
  };
}

type UnknownRecord = Record<string, unknown>;

function storage(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.sessionStorage;
}

function record(value: unknown): UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};
}

function stringValue(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function numberValue(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function resourceCode(value: string): ResourceCode | undefined {
  return value in resourceLabels ? (value as ResourceCode) : undefined;
}

function resources(
  values: readonly { resource_code: string; balance: number }[],
): ResourceDisplay[] {
  const balances = new Map(values.map((item) => [item.resource_code, item.balance]));
  return (Object.keys(resourceLabels) as ResourceCode[]).map((code) => ({
    code,
    label: resourceLabels[code],
    amount: balances.get(code) ?? 0,
  }));
}

function rewardResources(values: UnknownRecord): ResourceDisplay[] {
  return Object.entries(values).flatMap(([rawCode, rawAmount]) => {
    const code = resourceCode(rawCode);
    return code && typeof rawAmount === 'number'
      ? [{ code, label: resourceLabels[code], amount: rawAmount }]
      : [];
  });
}

function buildingType(code: string): BuildingType {
  const supported: readonly BuildingType[] = [
    'solar_shrine',
    'homes',
    'chinampa_farm',
    'stone_yard',
    'timber_workshop',
    'obsidian_workshop',
    'codex_house',
    'market',
    'dock',
    'garden',
  ];
  if (code === 'garden_plaza') return 'garden';
  return supported.includes(code as BuildingType) ? (code as BuildingType) : 'garden';
}

function displayName(code: string, config: UnknownRecord): string {
  const configured = config.display_name;
  if (typeof configured === 'string' && configured.length > 0) return configured;
  return code
    .split('_')
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function levelCost(config: UnknownRecord, level: number): ResourceDisplay[] {
  const levels = record(config.levels);
  const levelConfig = record(levels[String(level)]);
  return rewardResources(record(levelConfig.cost));
}

function levelVisualTier(config: UnknownRecord, level: number): 0 | 1 | 2 | 3 {
  const levels = record(config.levels);
  const levelConfig = record(levels[String(level)]);
  return Math.min(
    3,
    Math.max(0, numberValue(levelConfig.visual_tier, Math.floor((level - 1) / 2))),
  ) as 0 | 1 | 2 | 3;
}

function mapConstruction(item: {
  id: string;
  building_id: string;
  started_at: string;
  completes_at: string;
  status: string;
}) {
  const started = Date.parse(item.started_at);
  const completes = Date.parse(item.completes_at);
  const now = Date.now();
  const duration = Math.max(1, completes - started);
  const remainingSeconds = Math.max(0, Math.ceil((completes - now) / 1000));
  return {
    id: item.id,
    buildingId: item.building_id,
    completesAt: item.completes_at,
    progress: Math.min(1, Math.max(0, (now - started) / duration)),
    remainingLabel:
      remainingSeconds === 0
        ? 'Ready now'
        : remainingSeconds < 60
          ? `${remainingSeconds} sec`
          : remainingSeconds < 3600
            ? `${Math.ceil(remainingSeconds / 60)} min`
            : `${Math.floor(remainingSeconds / 3600)} h ${Math.ceil((remainingSeconds % 3600) / 60)} min`,
  };
}

export function mapBuilding(
  item: {
    id: string;
    code: string;
    level: number;
    x: number;
    y: number;
    footprint_width?: number;
    footprint_height?: number;
    rotation: number;
    skin_code: string;
    state: string;
    branch_code: string | null;
    district_code: string;
  },
  catalogs: ReadonlyMap<string, { max_level: number; config: UnknownRecord }>,
  constructionProgress: ReadonlyMap<string, number>,
  constructionTargetLevels: ReadonlyMap<string, number> = new Map(),
  constructionTargetBranches: ReadonlyMap<string, string | null> = new Map(),
): BuildingView {
  const catalog = catalogs.get(item.code);
  const constructing = item.state === 'constructing';
  const constructionTargetLevel = constructing
    ? (constructionTargetLevels.get(item.id) ?? item.level)
    : undefined;
  const availableBranches = branchPresentation[item.code] ?? [];
  const specializationLevel =
    availableBranches.length > 0
      ? Math.max(1, Math.floor(numberValue(catalog?.config.specialization_level, 3)))
      : undefined;
  return {
    id: item.id,
    type: buildingType(item.code),
    label: displayName(item.code, catalog?.config ?? {}),
    level: item.level,
    maxLevel: catalog?.max_level,
    constructionTargetLevel,
    upgradeDurationSeconds:
      catalog && item.level < catalog.max_level
        ? numberValue(
            record(record(catalog.config.levels)[String(item.level + 1)]).duration_seconds,
            0,
          )
        : undefined,
    gridX: item.x,
    gridY: item.y,
    footprintWidth: item.footprint_width,
    footprintHeight: item.footprint_height,
    orientation: item.rotation,
    variant: item.skin_code,
    districtCode: item.district_code,
    branchCode: item.branch_code ?? undefined,
    // The job supplies the target: foundations already store level 1, while
    // upgrades retain their previous level until authoritative completion.
    visualTier: catalog
      ? levelVisualTier(catalog.config, constructionTargetLevel ?? item.level)
      : 0,
    availableBranches,
    specializationLevel,
    state: constructing
      ? 'constructing'
      : catalog && item.level < catalog.max_level
        ? 'upgrade_available'
        : 'idle',
    progress: constructing ? (constructionProgress.get(item.id) ?? 0) : undefined,
    upgradeCost:
      catalog && item.level < catalog.max_level ? levelCost(catalog.config, item.level + 1) : [],
    benefit:
      'A permanent structure in your city. Completed levels count toward district expansion.',
    plannedCapabilities: catalog
      ? plannedBuildingCapabilities(
          catalog.config,
          constructionTargetLevel ?? Math.min(item.level + 1, catalog.max_level),
          (constructing ? constructionTargetBranches.get(item.id) : undefined) ??
            item.branch_code ??
            undefined,
        )
      : undefined,
    plannedBranchCapabilities: catalog
      ? Object.fromEntries(
          availableBranches.map((branch) => [
            branch.code,
            plannedBuildingCapabilities(catalog.config, item.level + 1, branch.code),
          ]),
        )
      : undefined,
  };
}

function discoveryState(value: string): DiscoveryMarkerView['state'] {
  const states: readonly DiscoveryMarkerView['state'][] = [
    'unknown',
    'nearby',
    'eligible',
    'queued',
    'claimed',
    'cooldown',
  ];
  return states.includes(value as DiscoveryMarkerView['state'])
    ? (value as DiscoveryMarkerView['state'])
    : 'unknown';
}

function rarity(value: string): DiscoveryMarkerView['rarity'] {
  return ['common', 'uncommon', 'rare', 'legendary'].includes(value)
    ? (value as DiscoveryMarkerView['rarity'])
    : 'common';
}

export function mapDiscovery(item: {
  id: string;
  player_discovery_id: string | null;
  kind: string;
  longitude: number;
  latitude: number;
  state: string;
  rarity: string;
  visual_family: string;
  available_until: string | null;
  world_node_id?: string | null;
  node_name?: string | null;
  node_kind?: string | null;
  node_tier?: number | null;
  activity_code?: string | null;
  activity_title?: string | null;
  activity_objective?: string | null;
  activity_mechanic?: string | null;
  route_role?: string | null;
  tier_effect?: string | null;
  decode_modifiers?: string[];
  reward_preview?: string[];
  attunement_level?: number;
  weave_role?: string | null;
}): DiscoveryMarkerView {
  const family = item.visual_family === 'unknown' ? 'mystery' : item.visual_family;
  const supportedKinds: readonly DiscoveryMarkerView['kind'][] = [
    'glyph_fragment',
    'ruin',
    'cache',
    'celestial',
    'tribe',
    'seasonal',
  ];
  return {
    id: item.id,
    kind: supportedKinds.includes(item.kind as DiscoveryMarkerView['kind'])
      ? (item.kind as DiscoveryMarkerView['kind'])
      : 'ruin',
    title: item.node_name ?? `${family.charAt(0).toUpperCase()}${family.slice(1)} world encounter`,
    description:
      item.activity_objective ??
      'A rotating local challenge awaits. Reach its small interaction radius safely, then restore and collect it.',
    longitude: item.longitude,
    latitude: item.latitude,
    state: discoveryState(item.state),
    rarity: rarity(item.rarity),
    visualFamily: family,
    availableUntil: item.available_until ?? undefined,
    worldNodeId: item.world_node_id ?? undefined,
    nodeName: item.node_name ?? undefined,
    nodeKind: worldNodeKind(item.node_kind),
    nodeTier: worldNodeTier(item.node_tier),
    activityCode: item.activity_code ?? undefined,
    activityTitle: item.activity_title ?? undefined,
    activityObjective: item.activity_objective ?? undefined,
    activityMechanic: item.activity_mechanic ?? undefined,
    routeRole: item.route_role ?? undefined,
    tierEffect: item.tier_effect ?? undefined,
    decodeModifiers: item.decode_modifiers ?? [],
    rewardPreview: item.reward_preview ?? [],
    attunementLevel: item.attunement_level === 0 ? 0 : (worldNodeTier(item.attunement_level) ?? 0),
    weaveRole:
      item.weave_role === 'origin' ||
      item.weave_role === 'destination' ||
      item.weave_role === 'woven'
        ? item.weave_role
        : undefined,
  };
}

export function mapEnergyLink(item: {
  id: string;
  source_node_id: string;
  destination_node_id: string;
  source_coordinates: number[];
  destination_coordinates: number[];
  family_code: string;
  required_tier: number;
  state: string;
}): EnergyLinkView {
  const states: readonly EnergyLinkView['state'][] = ['dormant', 'attuned', 'ready', 'awakened'];
  return {
    id: item.id,
    sourceNodeId: item.source_node_id,
    destinationNodeId: item.destination_node_id,
    sourceCoordinates: coordinatePair(item.source_coordinates),
    destinationCoordinates: coordinatePair(item.destination_coordinates),
    familyCode: item.family_code,
    requiredTier: worldNodeTier(item.required_tier) ?? 1,
    state: states.includes(item.state as EnergyLinkView['state'])
      ? (item.state as EnergyLinkView['state'])
      : 'dormant',
  };
}

export function mapPlayerWeave(item: {
  id: string;
  source_node_id: string;
  destination_node_id: string;
  source_name: string;
  destination_name: string;
  source_coordinates: number[];
  destination_coordinates: number[];
  route_coordinates: number[][];
  family_code: string;
  distance_m: number;
  status: string;
  resonance: number;
  planned_at: string;
  awakened_at: string | null;
  challenge_state?: KnowledgeWeaveView['challengeState'];
  visit_stage?: KnowledgeWeaveView['visitStage'];
  challenge_session_id?: string | null;
  route_kind?: KnowledgeWeaveView['routeKind'];
  route_attribution?: string | null;
  route_dataset_release?: string | null;
}): KnowledgeWeaveView {
  return {
    id: item.id,
    sourceNodeId: item.source_node_id,
    destinationNodeId: item.destination_node_id,
    sourceName: item.source_name,
    destinationName: item.destination_name,
    sourceCoordinates: coordinatePair(item.source_coordinates),
    destinationCoordinates: coordinatePair(item.destination_coordinates),
    routeCoordinates: item.route_coordinates.map(coordinatePair),
    routeKind: item.route_kind ?? 'direct_preview',
    routeAttribution: item.route_attribution ?? undefined,
    routeDatasetRelease: item.route_dataset_release ?? undefined,
    familyCode: item.family_code,
    distanceM: item.distance_m,
    status: item.status === 'awakened' || item.status === 'abandoned' ? item.status : 'planned',
    resonance: (worldNodeTier(item.resonance) ?? 1) as KnowledgeWeaveView['resonance'],
    plannedAt: item.planned_at,
    awakenedAt: item.awakened_at ?? undefined,
    challengeState: item.challenge_state,
    visitStage: item.visit_stage,
    challengeSessionId: item.challenge_session_id ?? undefined,
  };
}

export function mapWorldRoute(item: {
  id: string;
  title: string;
  family_code: string;
  distance_m: number;
  required_tier: number;
  state: string;
  current_step: number;
  next_node_id: string | null;
  stops: {
    sequence: number;
    node_id: string;
    name: string;
    node_kind: string;
    node_tier: number;
    coordinates: number[];
    state: string;
  }[];
}): WorldRouteContractView {
  const routeStates: readonly WorldRouteContractView['state'][] = [
    'available',
    'active',
    'completed',
  ];
  return {
    id: item.id,
    title: item.title,
    familyCode: item.family_code,
    distanceM: item.distance_m,
    requiredTier: worldNodeTier(item.required_tier) ?? 1,
    state: routeStates.includes(item.state as WorldRouteContractView['state'])
      ? (item.state as WorldRouteContractView['state'])
      : 'available',
    currentStep: item.current_step,
    nextNodeId: item.next_node_id ?? undefined,
    stops: item.stops.map((stop) => ({
      sequence: stop.sequence,
      nodeId: stop.node_id,
      name: stop.name,
      nodeKind: worldNodeKind(stop.node_kind) ?? 'glyph_archive',
      nodeTier: worldNodeTier(stop.node_tier) ?? 1,
      coordinates: coordinatePair(stop.coordinates),
      state:
        stop.state === 'visited' || stop.state === 'next' || stop.state === 'locked'
          ? stop.state
          : 'locked',
    })),
  };
}

function coordinatePair(value: number[]): readonly [number, number] {
  return [value[0] ?? 0, value[1] ?? 0];
}

function worldNodeKind(value: string | null | undefined): DiscoveryMarkerView['nodeKind'] {
  const kinds: readonly NonNullable<DiscoveryMarkerView['nodeKind']>[] = [
    'glyph_archive',
    'civic_nexus',
    'verdant_waypoint',
    'water_gate',
    'celestial_observatory',
    'market_exchange',
  ];
  return kinds.includes(value as NonNullable<DiscoveryMarkerView['nodeKind']>)
    ? (value as NonNullable<DiscoveryMarkerView['nodeKind']>)
    : undefined;
}

function worldNodeTier(value: number | null | undefined): DiscoveryMarkerView['nodeTier'] {
  return value && [1, 2, 3, 4, 5].includes(value)
    ? (value as NonNullable<DiscoveryMarkerView['nodeTier']>)
    : undefined;
}

function reviewStatus(value: string): CulturalReviewStatus {
  const supported: readonly CulturalReviewStatus[] = [
    'draft',
    'needs_cultural_review',
    'reviewed',
    'approved',
    'retired',
  ];
  return supported.includes(value as CulturalReviewStatus)
    ? (value as CulturalReviewStatus)
    : 'needs_cultural_review';
}

function codexStatus(value: string): CodexEntryView['status'] {
  const supported: readonly CodexEntryView['status'][] = [
    'unknown',
    'hinted',
    'found',
    'decoded',
    'mastered',
  ];
  return supported.includes(value as CodexEntryView['status'])
    ? (value as CodexEntryView['status'])
    : 'unknown';
}

function mapCodexFamily(family: {
  family_code: string;
  completion_count: number;
  entry_count: number;
  mastery_tier: string;
  entries: Array<{
    code: string;
    family_code: string;
    status: string;
    title?: string | null;
    icon_asset_key: string;
    lore_preview?: string | null;
    review_status: string;
    mastery_points: number;
  }>;
}): CodexFamilyView {
  const familyLabel = `${family.family_code.charAt(0).toUpperCase()}${family.family_code.slice(1)}`;
  const tier = `${family.mastery_tier.charAt(0).toUpperCase()}${family.mastery_tier.slice(1)}`;
  return {
    id: family.family_code,
    label: familyLabel,
    motif: family.family_code,
    decodedCount: family.completion_count,
    totalCount: family.entry_count,
    masteryTier: ['Awakening', 'Radiance', 'Ascension', 'Legend'].includes(tier)
      ? (tier as CodexFamilyView['masteryTier'])
      : 'Awakening',
    entries: family.entries.map((entry) => ({
      id: entry.code,
      familyId: entry.family_code,
      status: codexStatus(entry.status),
      title: entry.title ?? undefined,
      iconAssetKey: entry.icon_asset_key,
      lorePreview: entry.lore_preview ?? undefined,
      masteryProgress: Math.min(100, entry.mastery_points * 20),
      reviewStatus: reviewStatus(entry.review_status),
    })),
  };
}

function decodeProfileCode(value: string): 'tutorial' | 'common' | 'advanced' | 'mastery' {
  return ['tutorial', 'common', 'advanced', 'mastery'].includes(value)
    ? (value as 'tutorial' | 'common' | 'advanced' | 'mastery')
    : 'tutorial';
}

function mapSoloSummary(summary: {
  independent_of_tribe: boolean;
  current_rank: string;
  ranks: Array<{
    code: string;
    display_name: string;
    ordinal: number;
    eligible: boolean;
    requirements: Record<string, number>;
    progress: Record<string, number>;
  }>;
  contracts: Array<{
    id: string;
    code: string;
    specialization: string;
    event: string;
    current: number;
    target: number;
    status: string;
    rewards: Record<string, number>;
  }>;
  merchant_orders: Array<{
    id: string;
    code: string;
    specialization: string;
    resource_code: string;
    unit_cost: number;
    fulfilled_quantity: number;
    target_quantity: number;
    status: string;
    completion_rewards: Record<string, number>;
  }>;
}): SoloProgressionView {
  return {
    independentOfTribe: summary.independent_of_tribe,
    currentRank: summary.current_rank,
    ranks: summary.ranks.map((rank) => ({
      code: rank.code,
      displayName: rank.display_name,
      ordinal: rank.ordinal,
      eligible: rank.eligible,
      requirements: rank.requirements,
      progress: rank.progress,
    })),
    contracts: summary.contracts.map((contract) => ({
      id: contract.id,
      code: contract.code,
      specialization: contract.specialization,
      event: contract.event,
      current: contract.current,
      target: contract.target,
      status: contract.status,
      rewards: rewardResources(contract.rewards),
    })),
    merchantOrders: summary.merchant_orders.flatMap((order) => {
      const code = resourceCode(order.resource_code);
      if (!code) return [];
      return [
        {
          id: order.id,
          code: order.code,
          specialization: order.specialization,
          resourceCode: code,
          unitCost: order.unit_cost,
          fulfilledQuantity: order.fulfilled_quantity,
          targetQuantity: order.target_quantity,
          status: order.status,
          completionRewards: rewardResources(order.completion_rewards),
        },
      ];
    }),
  };
}

function tribeRole(value: string | null): TribeRole | undefined {
  return value && ['member', 'veteran', 'officer', 'leader'].includes(value)
    ? (value as TribeRole)
    : undefined;
}

function mapTribe(tribe: {
  id: string;
  name: string;
  slug: string;
  leader_member_id?: string;
  reputation: number;
  rank_tier: string;
  member_count: number;
  current_user_role?: string | null;
  project: {
    id: string;
    title: string;
    phase: number;
    phase_count: number;
    progress: number;
    state: string;
    remaining_seconds: number;
    visual_stage_asset_key: string;
    requirements: Array<{ resource_code: string; current: number; target: number }>;
  } | null;
  members?: Array<{
    member_id: string;
    display_name: string;
    role: string;
    contribution_score: number;
    joined_at: string;
  }>;
}): TribeSummaryView {
  return {
    id: tribe.id,
    name: tribe.name,
    slug: tribe.slug,
    leaderMemberId: tribe.leader_member_id,
    reputation: tribe.reputation,
    rankTier: tribe.rank_tier,
    memberCount: tribe.member_count,
    currentUserRole: tribeRole(tribe.current_user_role ?? null),
    project: tribe.project
      ? {
          id: tribe.project.id,
          title: tribe.project.title,
          phase: tribe.project.phase,
          phaseCount: tribe.project.phase_count,
          progress: tribe.project.progress,
          state: tribe.project.state,
          remainingSeconds: tribe.project.remaining_seconds,
          visualStageAssetKey: tribe.project.visual_stage_asset_key,
          requirements: tribe.project.requirements.flatMap((requirement) => {
            const code = resourceCode(requirement.resource_code);
            return code
              ? [
                  {
                    resourceCode: code,
                    label: resourceLabels[code],
                    current: requirement.current,
                    target: requirement.target,
                  },
                ]
              : [];
          }),
        }
      : undefined,
    members: (tribe.members ?? []).flatMap((member) => {
      const role = tribeRole(member.role);
      return role
        ? [
            {
              memberId: member.member_id,
              displayName: member.display_name,
              role,
              contributionScore: member.contribution_score,
              joinedAt: member.joined_at,
            },
          ]
        : [];
    }),
  };
}

function mapFeedEvent(event: {
  id: string;
  event_type: TribeFeedEventView['eventType'];
  actor_member_id: string | null;
  payload: Record<string, string | number | boolean>;
  created_at: string;
}): TribeFeedEventView {
  return {
    id: event.id,
    eventType: event.event_type,
    actorMemberId: event.actor_member_id ?? undefined,
    payload: event.payload,
    createdAt: event.created_at,
  };
}

function missionComplete(
  missions: readonly { event: string; current: number; target: number; status: string }[],
  event: string,
): boolean {
  return missions.some(
    (mission) =>
      mission.event === event &&
      (mission.status === 'completed' || mission.current >= mission.target),
  );
}

function missionSteps(
  missions: readonly { event: string; current: number; target: number; status: string }[],
): MissionStepView[] {
  return [
    {
      id: 'discover',
      label: 'Secure a nearby fragment',
      complete: missionComplete(missions, 'discovery_claimed'),
    },
    {
      id: 'decode',
      label: 'Restore its glyph',
      complete: missionComplete(missions, 'decode_completed'),
    },
    {
      id: 'build',
      label: 'Begin a settlement upgrade',
      complete: missionComplete(missions, 'construction_started'),
    },
  ];
}

function decodeTutorialSteps(definition: UnknownRecord): readonly DecodeTutorialStepView[] {
  if (!Array.isArray(definition.tutorial_steps)) return [];
  return definition.tutorial_steps.flatMap((value, index) => {
    const step = record(value);
    const segmentId = typeof step.segment_id === 'string' ? step.segment_id : '';
    const direction =
      step.direction === 'counter_clockwise'
        ? ('counter_clockwise' as const)
        : step.direction === 'clockwise'
          ? ('clockwise' as const)
          : undefined;
    const concept =
      step.concept === 'wake_source' ||
      step.concept === 'carry_energy' ||
      step.concept === 'reach_seal'
        ? step.concept
        : undefined;
    if (!segmentId || !direction || !concept) return [];
    return [
      {
        sequence: numberValue(step.sequence, index + 1),
        segmentId,
        direction,
        concept,
      },
    ];
  });
}

export function decodeDefinition(session: {
  id: string;
  source_discovery_id?: string | null;
  source_remote_expedition_id?: string | null;
  source_weave_id?: string | null;
  source_civic_expedition_id?: string | null;
  source_personal_encounter_id?: string | null;
  reward_mode?: 'standard' | 'weave_only' | 'civic_only' | 'personal_materials';
  status: string;
  profile_code: string;
  family_code: string;
  entry_code: string;
  hint_count: number;
  move_count: number;
  definition: UnknownRecord;
  current_orientations?: UnknownRecord;
  result?: UnknownRecord | null;
}): DecodeRunView {
  const definition = record(session.definition);
  const definitionVersion = numberValue(definition.definition_version, 0);
  const rawSegments = Array.isArray(definition.segments) ? definition.segments : [];
  const locked = new Set(
    Array.isArray(definition.locked_segments)
      ? definition.locked_segments.filter((value): value is string => typeof value === 'string')
      : [],
  );
  const familyLabel = `${session.family_code.charAt(0).toUpperCase()}${session.family_code.slice(1)}`;
  const result = record(session.result);
  const currentOrientations = record(session.current_orientations);
  return {
    runId: session.id,
    sourceDiscoveryId: session.source_discovery_id ?? undefined,
    sourceRemoteExpeditionId: session.source_remote_expedition_id ?? undefined,
    sourceWeaveId: session.source_weave_id ?? undefined,
    sourceCivicExpeditionId: session.source_civic_expedition_id ?? undefined,
    sourcePersonalEncounterId: session.source_personal_encounter_id ?? undefined,
    rewardMode: session.reward_mode ?? 'standard',
    weaveAwakened: result.weave_awakened === true,
    status: session.status === 'completed' ? 'solved' : 'active',
    currentMoves: session.move_count,
    hintCount: session.hint_count,
    profileCode: decodeProfileCode(session.profile_code),
    modifiers: Array.isArray(definition.modifiers)
      ? definition.modifiers.filter((value): value is string => typeof value === 'string')
      : [],
    masteryTargets: Array.isArray(definition.mastery_targets)
      ? definition.mastery_targets.filter((value): value is string => typeof value === 'string')
      : ['solve'],
    puzzle: {
      id: session.entry_code,
      definitionVersion,
      seed: stringValue(definition.seed, 'server-private-seed'),
      familyId: session.family_code,
      familyLabel,
      ringCount: numberValue(definition.ring_count, 2),
      sectorsPerRing: Array.isArray(definition.sectors_per_ring)
        ? definition.sectors_per_ring.map((value) => numberValue(value, 1))
        : [3, 4],
      terminalSegmentIds: Array.isArray(definition.target_terminals)
        ? definition.target_terminals.filter((value): value is string => typeof value === 'string')
        : [],
      sourceSegmentIds: Array.isArray(record(definition.rule_state).source_segment_ids)
        ? (record(definition.rule_state).source_segment_ids as unknown[]).filter(
            (value): value is string => typeof value === 'string',
          )
        : undefined,
      rotatableRingIndices: Array.isArray(record(definition.rule_state).rotatable_rings)
        ? (record(definition.rule_state).rotatable_rings as unknown[])
            .map((value) => numberValue(value, -1))
            .filter((value) => value >= 0)
        : undefined,
      tutorialSteps:
        session.profile_code === 'tutorial' ? decodeTutorialSteps(definition) : undefined,
      moveLimit: typeof definition.move_limit === 'number' ? definition.move_limit : undefined,
      parMoves: typeof definition.par_moves === 'number' ? definition.par_moves : undefined,
      minimumSolutionMoves:
        typeof definition.minimum_solution_moves === 'number'
          ? definition.minimum_solution_moves
          : undefined,
      difficultyEstimate:
        typeof definition.difficulty_estimate === 'number'
          ? definition.difficulty_estimate
          : undefined,
      reviewStatus: 'needs_cultural_review',
      segments: rawSegments.map((value, index) => {
        const segment = record(value);
        const rawPorts = record(segment.ports);
        const ports = Object.values(rawPorts).filter((port) => port !== null).length;
        const channelShape = stringValue(segment.channel_shape, '');
        const channelTypes = Object.fromEntries(
          [
            ['inner', rawPorts.inner],
            ['clockwise', rawPorts.cw],
            ['outer', rawPorts.outer],
            ['counter_clockwise', rawPorts.ccw],
          ].filter(
            (entry): entry is [string, 'jade' | 'gold'] =>
              entry[1] === 'jade' || entry[1] === 'gold',
          ),
        );
        return {
          id: stringValue(segment.segment_id, `r0s${index}`),
          ringIndex: numberValue(segment.ring_index, 0),
          angularSlot: numberValue(segment.angular_slot, index),
          rotation: numberValue(
            currentOrientations[stringValue(segment.segment_id, '')],
            numberValue(segment.rotation, 0),
          ),
          locked: locked.has(stringValue(segment.segment_id, '')),
          channelShape:
            channelShape === 'corner' || channelShape === 'fork' || channelShape === 'straight'
              ? channelShape
              : ports >= 3
                ? ('fork' as const)
                : ('straight' as const),
          channelTypes: definitionVersion >= 3 ? channelTypes : undefined,
        };
      }),
    },
    rewardPreview:
      session.reward_mode === 'weave_only' || session.reward_mode === 'civic_only'
        ? []
        : rewardResources(record(result.rewards)),
  };
}

/** Select a side challenge without replacing the personal session return point. */
export function selectPersonalRestorationSession(sessionId: string): void {
  storage()?.setItem(DECODE_SESSION_KEY, sessionId);
  storage()?.setItem(DECODE_SELECTED_SESSION_KEY, sessionId);
  storage()?.removeItem(DECODE_DISPLAYED_SESSION_KEY);
}

export function selectCivicDecodeSession(sessionId: string): void {
  const store = storage();
  if (!store) return;
  if (!store.getItem(DECODE_CIVIC_SESSION_KEY)) {
    const personal =
      store.getItem(DECODE_SELECTED_SESSION_KEY) ?? store.getItem(DECODE_SESSION_KEY);
    if (personal) store.setItem(DECODE_PERSONAL_RETURN_KEY, personal);
  }
  store.setItem(DECODE_CIVIC_SESSION_KEY, sessionId);
  store.setItem(DECODE_SELECTED_SESSION_KEY, sessionId);
  store.removeItem(DECODE_DISPLAYED_SESSION_KEY);
}

function restorePersonalDecodeSelection(): void {
  const store = storage();
  const personal = store?.getItem(DECODE_PERSONAL_RETURN_KEY);
  store?.removeItem(DECODE_CIVIC_SESSION_KEY);
  store?.removeItem(DECODE_PERSONAL_RETURN_KEY);
  store?.removeItem(DECODE_SELECTED_SESSION_KEY);
  store?.removeItem(DECODE_DISPLAYED_SESSION_KEY);
  if (personal) store?.setItem(DECODE_SELECTED_SESSION_KEY, personal);
}

export function releaseDisplayedDecodeSelection(current?: DecodeRunView): boolean {
  const selectedId = storage()?.getItem(DECODE_SELECTED_SESSION_KEY);
  const displayedId = storage()?.getItem(DECODE_DISPLAYED_SESSION_KEY);
  if (!selectedId || selectedId !== displayedId) return false;
  if (
    current?.runId === selectedId &&
    current.rewardMode === 'personal_materials' &&
    current.status !== 'solved'
  )
    return false;
  if (selectedId === storage()?.getItem(DECODE_CIVIC_SESSION_KEY)) {
    restorePersonalDecodeSelection();
    return true;
  }
  storage()?.removeItem(DECODE_SELECTED_SESSION_KEY);
  storage()?.removeItem(DECODE_DISPLAYED_SESSION_KEY);
  return true;
}

export type DailyDecodeEntryResult =
  | { kind: 'opened' | 'resume'; decode: DecodeRunView }
  | { kind: 'complete'; availableAgainAt: string }
  | { kind: 'stale' };

/** Route entry only. A completion refetch must keep the just-solved board visible. */
export async function openDailyDecodeOnEntry({
  entryRunId,
  commandId,
  canContinue,
  client,
  completedUntil,
}: {
  entryRunId: string;
  commandId: string;
  canContinue: () => boolean;
  client?: ApiClient;
  completedUntil?: string;
}): Promise<DailyDecodeEntryResult> {
  const current = () => {
    const selected = storage()?.getItem(DECODE_SELECTED_SESSION_KEY);
    return canContinue() && (!selected || selected === entryRunId);
  };
  if (!current()) return { kind: 'stale' };
  const api = client ?? apiClient({ mutation: true });
  const response = await api.GET('/api/v1/decode/sessions/current');
  if (!current()) return { kind: 'stale' };
  const resumed = required(
    response.data,
    response.error,
    'Your current glyph could not be checked.',
  );
  const select = (session: ApiComponents['schemas']['DecodeSessionResponse']) => {
    storage()?.setItem(DECODE_SESSION_KEY, session.id);
    storage()?.setItem(DECODE_SELECTED_SESSION_KEY, session.id);
    storage()?.setItem(DECODE_DISPLAYED_SESSION_KEY, session.id);
    return decodeDefinition(session);
  };
  // A stale completed snapshot must never displace unfinished server-owned work.
  if (resumed?.status === 'active') return { kind: 'resume', decode: select(resumed) };
  if (completedUntil && Date.parse(completedUntil) > Date.now())
    return { kind: 'complete', availableAgainAt: completedUntil };
  const expeditionResponse = await api.POST('/api/v1/explore/remote-expeditions/daily', {
    params: { header: { 'Idempotency-Key': commandId } },
    body: {},
  });
  if (!current()) return { kind: 'stale' };
  const expedition = required(
    expeditionResponse.data,
    expeditionResponse.error,
    'Today’s Daily Codex could not be opened.',
  );
  if (expedition.status === 'completed') {
    if (!Number.isFinite(Date.parse(expedition.available_again_at)))
      throw new Error(
        'Daily Codex availability is temporarily unavailable. Reopen Decode to try again.',
      );
    return { kind: 'complete', availableAgainAt: expedition.available_again_at };
  }
  const decodeResponse = await api.POST('/api/v1/decode/sessions', {
    body: {
      source_remote_expedition_id: expedition.id,
      profile_code: expedition.profile_code,
    },
  });
  if (!current()) return { kind: 'stale' };
  const session = required(
    decodeResponse.data,
    decodeResponse.error,
    'The daily glyph could not begin.',
  );
  return { kind: 'opened', decode: select(session) };
}

export async function currentDecode(
  client: ApiClient,
  claimedPlayerDiscoveryId?: string,
  signal?: AbortSignal,
): Promise<DecodeRunView> {
  signal?.throwIfAborted();
  const viewingDecode = typeof window !== 'undefined' && window.location.pathname === '/decode';
  const selectedId = storage()?.getItem(DECODE_SELECTED_SESSION_KEY);
  if (selectedId) {
    const response = await client.GET('/api/v1/decode/sessions/{session_id}', {
      params: { path: { session_id: selectedId } },
      signal,
    });
    signal?.throwIfAborted();
    if (storage()?.getItem(DECODE_SELECTED_SESSION_KEY) !== selectedId) {
      return currentDecode(client, claimedPlayerDiscoveryId, signal);
    }
    if (response.data) {
      const leavingOrdinaryRun =
        response.data.reward_mode !== 'weave_only' &&
        !(
          response.data.reward_mode === 'personal_materials' && response.data.status === 'active'
        ) &&
        !viewingDecode &&
        storage()?.getItem(DECODE_DISPLAYED_SESSION_KEY) === selectedId;
      if (leavingOrdinaryRun) {
        if (response.data.reward_mode === 'civic_only') {
          restorePersonalDecodeSelection();
          return currentDecode(client, claimedPlayerDiscoveryId, signal);
        }
        storage()?.removeItem(DECODE_SELECTED_SESSION_KEY);
        storage()?.removeItem(DECODE_DISPLAYED_SESSION_KEY);
      } else {
        // Keep the displayed run through completion/refetch. The server's ordinary
        // current session may intentionally be a different unfinished challenge.
        if (viewingDecode && response.data.reward_mode !== 'weave_only') {
          storage()?.setItem(DECODE_DISPLAYED_SESSION_KEY, response.data.id);
        }
        if (response.data.reward_mode !== 'civic_only') {
          storage()?.setItem(DECODE_SESSION_KEY, response.data.id);
        }
        return decodeDefinition(response.data);
      }
    }
    if (selectedId === storage()?.getItem(DECODE_CIVIC_SESSION_KEY)) {
      // Expired contracts or lost membership must not strand the personal puzzle.
      restorePersonalDecodeSelection();
      return currentDecode(client, claimedPlayerDiscoveryId, signal);
    }
    storage()?.removeItem(DECODE_SELECTED_SESSION_KEY);
  }

  signal?.throwIfAborted();
  const selectionBeforeCurrent = storage()?.getItem(DECODE_SELECTED_SESSION_KEY);
  const currentResponse = await client.GET('/api/v1/decode/sessions/current', { signal });
  signal?.throwIfAborted();
  if (
    storage()?.getItem(DECODE_SELECTED_SESSION_KEY) !== selectionBeforeCurrent ||
    viewingDecode !== (typeof window !== 'undefined' && window.location.pathname === '/decode')
  ) {
    return currentDecode(client, claimedPlayerDiscoveryId, signal);
  }
  if (currentResponse.data) {
    storage()?.setItem(DECODE_SESSION_KEY, currentResponse.data.id);
    if (viewingDecode && currentResponse.data.reward_mode !== 'weave_only') {
      storage()?.setItem(DECODE_SELECTED_SESSION_KEY, currentResponse.data.id);
      storage()?.setItem(DECODE_DISPLAYED_SESSION_KEY, currentResponse.data.id);
    }
    return decodeDefinition(currentResponse.data);
  }

  const mutationClient = apiClient({ mutation: true });
  signal?.throwIfAborted();
  const response = await mutationClient.POST('/api/v1/decode/sessions', {
    body: claimedPlayerDiscoveryId
      ? { source_discovery_id: claimedPlayerDiscoveryId, profile_code: 'common' }
      : { profile_code: 'tutorial' },
    signal,
  });
  const session = required(
    response.data,
    response.error,
    'A Decode session could not be prepared.',
  );
  storage()?.setItem(DECODE_SESSION_KEY, session.id);
  return decodeDefinition(session);
}

export async function loadApiVerticalSlice(signal?: AbortSignal): Promise<PlayerVerticalSliceView> {
  const client = apiClient();
  // Bootstrap provisions player and mission projections. Complete it before
  // read fan-out so a first login cannot race two provisioning writes.
  const bootstrapResult = await client.GET('/api/v1/bootstrap', { signal });
  const bootstrap = required(
    bootstrapResult.data,
    bootstrapResult.error,
    'Your player state could not be loaded.',
  );
  signal?.throwIfAborted();
  const [
    settlementResult,
    catalogResult,
    codexResult,
    missionsResult,
    historyResult,
    viewportResult,
    profilesResult,
    masteryResult,
    soloResult,
    tribeDiscoverResult,
    currentTribeResult,
  ] = await Promise.all([
    client.GET('/api/v1/settlement', { signal }),
    client.GET('/api/v1/settlement/buildings/catalog', { signal }),
    client.GET('/api/v1/codex', { signal }),
    client.GET('/api/v1/missions', { signal }),
    client.GET('/api/v1/explore/history/summary', { signal }),
    client.GET('/api/v1/explore/viewport', {
      params: { query: { bbox: VIEWPORT_BBOX } },
      signal,
    }),
    client.GET('/api/v1/decode/profiles', { signal }),
    client.GET('/api/v1/decode/mastery', { signal }),
    client.GET('/api/v1/solo', { signal }),
    client.GET('/api/v1/tribes/discover', { params: { query: { limit: 20 } }, signal }),
    client.GET('/api/v1/tribes/me', { signal }),
  ]);
  signal?.throwIfAborted();
  const settlement = required(
    settlementResult.data,
    settlementResult.error,
    'Your settlement could not be loaded.',
  );
  const catalog = required(
    catalogResult.data,
    catalogResult.error,
    'The building catalog could not be loaded.',
  );
  const codex = required(codexResult.data, codexResult.error, 'The Codex could not be loaded.');
  const missions = required(
    missionsResult.data,
    missionsResult.error,
    'Mission progress could not be loaded.',
  );
  const history = required(
    historyResult.data,
    historyResult.error,
    'Exploration history could not be loaded.',
  );
  const viewport = required(
    viewportResult.data,
    viewportResult.error,
    'Nearby discoveries could not be loaded.',
  );
  const profiles = required(
    profilesResult.data,
    profilesResult.error,
    'Decode profiles could not be loaded.',
  );
  const mastery = required(
    masteryResult.data,
    masteryResult.error,
    'Decode mastery could not be loaded.',
  );
  const solo = required(soloResult.data, soloResult.error, 'Solo progression could not be loaded.');
  const tribeDiscover = required(
    tribeDiscoverResult.data,
    tribeDiscoverResult.error,
    'Tribe projects could not be loaded.',
  );
  const currentTribeAuthority = required(
    currentTribeResult.data,
    currentTribeResult.error,
    'Your current Tribe membership could not be verified.',
  );
  const activeConstruction = settlement.construction
    .filter((item) => item.status === 'active')
    .map(mapConstruction);
  const constructionProgress = new Map(
    activeConstruction.map((item) => [item.buildingId, item.progress]),
  );
  const constructionTargetLevels = new Map(
    settlement.construction
      .filter((item) => item.status === 'active')
      .map((item) => [item.building_id, item.to_level]),
  );
  const constructionTargetBranches = new Map(
    settlement.construction
      .filter((item) => item.status === 'active')
      .map((item) => [item.building_id, item.target_branch_code]),
  );
  const catalogs = new Map(
    catalog.buildings.map((item) => [
      item.code,
      { max_level: item.max_level, config: record(item.config) },
    ]),
  );
  const discoveries = viewport.discoveries.map(mapDiscovery);
  const claimedPlayerDiscoveryId = viewport.discoveries.find(
    (item) => item.state === 'claimed' && item.player_discovery_id,
  )?.player_discovery_id;
  const decode = await currentDecode(client, claimedPlayerDiscoveryId ?? undefined, signal);
  const steps = missionSteps(missions.missions);
  const activeJourneyId = storage()?.getItem(JOURNEY_SESSION_KEY) ?? undefined;
  let currentTribe: TribeSummaryView | undefined;
  let tribeFeed: TribeFeedEventView[] = [];
  let tribeLeaderboard: PlayerVerticalSliceView['tribe']['leaderboard'] = [];
  let tribeLeaderboardPeriod: string | undefined;
  const authoritativeCurrentTribe = resolveAuthoritativeCurrentTribe(
    currentTribeAuthority,
    storage(),
  );
  if (authoritativeCurrentTribe) {
    const currentTribeId = authoritativeCurrentTribe.id;
    currentTribe = mapTribe(authoritativeCurrentTribe);
    const [feedResult, leaderboardResult] = await Promise.all([
      client.GET('/api/v1/tribes/{tribe_id}/feed', {
        params: { path: { tribe_id: currentTribeId }, query: { limit: 50 } },
        signal,
      }),
      client.GET('/api/v1/leaderboards/{board_code}', {
        params: {
          path: { board_code: 'weekly_tribe_contribution' },
          query: { tribe_id: currentTribeId, limit: 20 },
        },
        signal,
      }),
    ]);
    if (feedResult.data) tribeFeed = feedResult.data.events.map(mapFeedEvent);
    if (leaderboardResult.data) {
      tribeLeaderboard = leaderboardResult.data.entries.map((entry) => ({
        rank: entry.rank,
        entityId: entry.entity_id,
        displayName: entry.display_name,
        score: entry.score,
      }));
      tribeLeaderboardPeriod = leaderboardResult.data.period_key;
    }
  }
  return {
    schemaVersion: `bootstrap.v${bootstrap.schema_version}`,
    source: 'api',
    profile: {
      userId: bootstrap.profile.user_id,
      displayName: bootstrap.profile.display_name,
      level: bootstrap.progression.level,
      locale: bootstrap.profile.locale,
      avatarAssetId: bootstrap.profile.avatar_asset_id,
      avatarAssetUrl: bootstrap.profile.avatar_asset_url,
      avatarLabel: bootstrap.profile.avatar_label,
      onboardingStep: bootstrap.progression.onboarding_step,
      onboardingCompleted: bootstrap.progression.onboarding_completed,
    },
    settlement: {
      settlementId: settlement.id,
      name: settlement.name,
      level: Math.max(1, settlement.tier + 1),
      visualTier: Math.min(3, Math.max(0, settlement.tier)) as 0 | 1 | 2 | 3,
      biome: 'jade_coast_lake',
      resources: resources(bootstrap.balances),
      buildings: settlement.buildings.map((item) =>
        mapBuilding(
          item,
          catalogs,
          constructionProgress,
          constructionTargetLevels,
          constructionTargetBranches,
        ),
      ),
      construction: activeConstruction,
      districts: settlement.districts.map(mapSettlementDistrict),
      history: settlement.history.map(mapSettlementHistoryEvent),
      worldCapabilities: settlement.world_capabilities.map(mapWorldCapability),
      ambientPopulation: Math.max(5, settlement.buildings.length * 3),
      activeMission: {
        id: 'onboarding-restoration',
        title: 'The first restoration',
        description: 'Explore, decode and begin one visible settlement upgrade.',
        progress: steps.filter((step) => step.complete).length,
        target: steps.length,
        steps,
        rewardLabel: 'Server-awarded mission bundle',
      },
    },
    explore: {
      regionName: 'Jade Coast',
      center: [4.8952, 52.3702],
      interactionRadiusM: viewport.interaction_radius_m,
      maxInteractionAccuracyM: viewport.max_interaction_accuracy_m,
      uniqueCells: history.unique_cells,
      journeyActive: Boolean(activeJourneyId),
      journeyId: activeJourneyId,
      discoveries,
      energyLinks: viewport.energy_links.map(mapEnergyLink),
      weaves: (viewport.weaves ?? []).map(mapPlayerWeave),
      routeContracts: viewport.route_contracts.map(mapWorldRoute),
    },
    decode,
    decodeProfiles: profiles.profiles.map((profile) => ({
      code: decodeProfileCode(profile.code),
      version: profile.version,
      ringCount: profile.ring_count,
      sectorsPerRing: profile.sectors_per_ring,
      modifiers: profile.modifiers,
      masteryTargets: profile.mastery_targets,
      minimumSolutionMoves: profile.minimum_solution_moves,
    })),
    decodeMastery: mastery.mastery.map((entry) => ({
      familyCode: entry.family_code,
      profileCode: entry.profile_code,
      attempts: entry.attempts,
      completions: entry.completions,
      noHintCompletions: entry.no_hint_completions,
      bestScore: entry.best_score,
      masteryPoints: entry.mastery_points,
      masteryTier: entry.mastery_tier,
    })),
    codexFamilies: codex.families.map(mapCodexFamily),
    soloPaths: [
      {
        id: 'explorer',
        label: 'Explorer',
        description: 'Reveal distinct regions and complete safe expedition chains.',
        progress: Math.min(100, history.unique_cells * 8),
        nextMilestone: 'Chart three unique cells',
      },
      {
        id: 'artisan',
        label: 'Artisan',
        description: 'Restore complex glyphs and improve civic craft.',
        progress: Math.min(100, bootstrap.progression.solo_reputation * 5),
        nextMilestone: 'Decode the next Codex path',
      },
      {
        id: 'merchant',
        label: 'Merchant',
        description: 'Fulfil independent orders without relying on a Tribe.',
        progress: Math.min(100, bootstrap.progression.settlement_tier * 10),
        nextMilestone: 'Open the market terrace',
      },
    ],
    solo: mapSoloSummary(solo),
    missions: missions.missions.map((mission) => ({
      id: mission.id,
      code: mission.code,
      scope: mission.scope,
      event: mission.event,
      current: mission.current,
      target: mission.target,
      status: mission.status,
      periodKey: mission.period_key,
    })),
    tribe: {
      discovered: tribeDiscover.tribes.map(mapTribe),
      current: currentTribe,
      feed: tribeFeed,
      leaderboard: tribeLeaderboard,
      leaderboardPeriod: tribeLeaderboardPeriod,
    },
    featureFlags: {
      exploreEnabled: bootstrap.feature_flags.explore_enabled ?? false,
      tribesEnabled: bootstrap.feature_flags.tribes_enabled ?? false,
      paymentsEnabled: bootstrap.feature_flags.payments_enabled ?? false,
      rewardExternalizationEnabled: false,
    },
  };
}

async function createDecodeForClaim(
  playerDiscoveryId: string,
  profileCode: 'common' | 'advanced' | 'mastery',
): Promise<void> {
  const client = apiClient({ mutation: true });
  const response = await client.POST('/api/v1/decode/sessions', {
    body: { source_discovery_id: playerDiscoveryId, profile_code: profileCode },
  });
  const session = required(response.data, response.error, 'Decode could not begin.');
  storage()?.setItem(DECODE_SESSION_KEY, session.id);
  storage()?.setItem(DECODE_SELECTED_SESSION_KEY, session.id);
}

export async function submitApiVerticalSliceCommand(
  current: PlayerVerticalSliceView,
  command: VerticalSliceCommand,
): Promise<PlayerVerticalSliceView> {
  const client = apiClient({ mutation: true });

  if (command.type === 'explore.claim') {
    const response = await client.POST('/api/v1/explore/discoveries/{discovery_id}/claim', {
      params: {
        path: { discovery_id: command.discoveryId },
        header: { 'Idempotency-Key': command.commandId },
      },
    });
    const claim = required(response.data, response.error, 'This discovery could not be secured.');
    await createDecodeForClaim(claim.player_discovery_id, claim.profile_code);
    return loadApiVerticalSlice();
  }

  if (command.type === 'explore.remote') {
    const expeditionResponse = await client.POST('/api/v1/explore/remote-expeditions/daily', {
      params: { header: { 'Idempotency-Key': command.commandId } },
      body: {},
    });
    const expedition = required(
      expeditionResponse.data,
      expeditionResponse.error,
      'The daily Codex expedition could not be prepared.',
    );
    if (expedition.status === 'completed') {
      throw new Error(
        `Today's Codex expedition is complete. Another becomes available at ${new Date(
          expedition.available_again_at,
        ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`,
      );
    }
    // Creating Decode sessions is source-idempotent on the server. Posting the same remote
    // expedition therefore resumes its existing session after a refresh or lost browser key
    // instead of exposing an internal "already started" state to the player.
    const decodeResponse = await client.POST('/api/v1/decode/sessions', {
      body: {
        source_remote_expedition_id: expedition.id,
        profile_code: expedition.profile_code,
      },
    });
    const session = required(
      decodeResponse.data,
      decodeResponse.error,
      'The expedition Decode could not begin.',
    );
    storage()?.setItem(DECODE_SESSION_KEY, session.id);
    storage()?.setItem(DECODE_SELECTED_SESSION_KEY, session.id);
    return loadApiVerticalSlice();
  }

  if (command.type === 'decode.weave.leave') {
    storage()?.removeItem(DECODE_SELECTED_SESSION_KEY);
    return loadApiVerticalSlice();
  }

  if (command.type === 'decode.weave.start') {
    const response = await client.POST('/api/v1/decode/sessions', {
      body: { source_weave_id: command.weaveId, profile_code: 'common' },
    });
    const session = required(response.data, response.error, 'The Weave challenge could not begin.');
    storage()?.setItem(DECODE_SELECTED_SESSION_KEY, session.id);
    return loadApiVerticalSlice();
  }

  if (command.type === 'decode.start') {
    const response = await client.POST('/api/v1/decode/sessions', {
      body: { profile_code: command.profileCode },
    });
    const session = required(response.data, response.error, 'Decode could not begin.');
    storage()?.setItem(DECODE_SESSION_KEY, session.id);
    storage()?.setItem(DECODE_SELECTED_SESSION_KEY, session.id);
    return loadApiVerticalSlice();
  }

  if (command.type === 'decode.hint') {
    const response = await client.POST('/api/v1/decode/sessions/{session_id}/hint', {
      params: {
        path: { session_id: current.decode.runId },
        header: { 'Idempotency-Key': command.commandId },
      },
      body: { moves: command.moves.map(apiMove) },
    });
    const hint = required(response.data, response.error, 'A Decode hint could not be prepared.');
    return {
      ...current,
      decode: {
        ...current.decode,
        hintCount: hint.hint_count,
        suggestedMove:
          hint.move_type === 'rotate_segment' && hint.segment_id
            ? {
                segmentId: hint.segment_id,
                direction:
                  hint.direction === 'counter_clockwise' ? 'counter_clockwise' : 'clockwise',
                completesPuzzle: hint.completes_puzzle,
              }
            : undefined,
      },
    };
  }

  if (command.type === 'decode.complete') {
    const response = await client.POST('/api/v1/decode/sessions/{session_id}/complete', {
      params: {
        path: { session_id: current.decode.runId },
        header: { 'Idempotency-Key': command.commandId },
      },
      body: {
        moves: command.moves.map(apiMove),
        client_completion_time_ms: Math.max(100, command.clientCompletionTimeMs),
        client_version: CLIENT_VERSION,
        integrity: { input: 'phaser_and_accessible_controls' },
      },
    });
    required(response.data, response.error, 'The server rejected this restoration.');
    storage()?.setItem(DECODE_SELECTED_SESSION_KEY, current.decode.runId);
    if (
      typeof window !== 'undefined' &&
      window.location.pathname === '/decode' &&
      current.decode.rewardMode !== 'weave_only'
    ) {
      storage()?.setItem(DECODE_DISPLAYED_SESSION_KEY, current.decode.runId);
    }
    return loadApiVerticalSlice();
  }

  if (command.type === 'settlement.upgrade') {
    const response = await client.POST('/api/v1/settlement/buildings/{building_id}/upgrade', {
      params: {
        path: { building_id: command.buildingId },
        header: { 'Idempotency-Key': command.commandId },
      },
      body: { branch_code: command.branchCode },
    });
    required(response.data, response.error, 'The building upgrade could not begin.');
    return loadApiVerticalSlice();
  }

  if (command.type === 'settlement.complete') {
    const response = await client.POST('/api/v1/settlement/construction/{job_id}/claim', {
      params: { path: { job_id: command.constructionId } },
    });
    required(response.data, response.error, 'Construction is not ready to complete.');
    return loadApiVerticalSlice();
  }

  if (command.type === 'solo.contract.claim') {
    const response = await client.POST('/api/v1/solo/contracts/{contract_id}/claim', {
      params: {
        path: { contract_id: command.contractId },
        header: { 'Idempotency-Key': command.commandId },
      },
    });
    required(response.data, response.error, 'The contract reward could not be claimed.');
    return loadApiVerticalSlice();
  }

  if (command.type === 'solo.order.fulfill') {
    const response = await client.POST('/api/v1/solo/orders/{order_id}/fulfill', {
      params: {
        path: { order_id: command.orderId },
        header: { 'Idempotency-Key': command.commandId },
      },
      body: { quantity: command.quantity },
    });
    required(response.data, response.error, 'The merchant order could not be fulfilled.');
    return loadApiVerticalSlice();
  }

  if (command.type === 'tribe.create') {
    const response = await client.POST('/api/v1/tribes', { body: { name: command.name } });
    const tribe = required(response.data, response.error, 'The Tribe could not be founded.');
    storage()?.setItem(TRIBE_PRESENTATION_HINT_KEY, tribe.id);
    return loadApiVerticalSlice();
  }

  if (command.type === 'tribe.join') {
    const response = await client.POST('/api/v1/tribes/{tribe_id}/join', {
      params: { path: { tribe_id: command.tribeId } },
    });
    const tribe = required(response.data, response.error, 'The Tribe could not be joined.');
    storage()?.setItem(TRIBE_PRESENTATION_HINT_KEY, tribe.id);
    return loadApiVerticalSlice();
  }

  if (command.type === 'tribe.leave') {
    const response = await client.POST('/api/v1/tribes/{tribe_id}/leave', {
      params: { path: { tribe_id: command.tribeId } },
    });
    if (response.error) throw new Error('The Tribe could not be left.');
    storage()?.removeItem(TRIBE_PRESENTATION_HINT_KEY);
    return loadApiVerticalSlice();
  }

  if (command.type === 'tribe.member.role') {
    const response = await client.PATCH('/api/v1/tribes/{tribe_id}/members/{member_id}', {
      params: { path: { tribe_id: command.tribeId, member_id: command.memberId } },
      body: { role: command.role },
    });
    required(response.data, response.error, 'The member role could not be changed.');
    return loadApiVerticalSlice();
  }

  const response = await client.POST('/api/v1/tribes/{tribe_id}/projects/{project_id}/contribute', {
    params: {
      path: { tribe_id: command.tribeId, project_id: command.projectId },
      header: { 'Idempotency-Key': command.commandId },
    },
    body: { resource_code: command.resourceCode, amount: command.amount },
  });
  required(response.data, response.error, 'The civic contribution could not be recorded.');
  return loadApiVerticalSlice();
}

function apiMove(move: DecodeMoveView) {
  if (move.moveType === 'rotate_ring') {
    if (move.ringIndex === undefined) throw new Error('A Decode ring move has no ring.');
    return {
      move_type: 'rotate_ring' as const,
      ring_index: move.ringIndex,
      direction: move.direction,
      client_elapsed_ms: move.clientElapsedMs,
    };
  }
  if (!move.segmentId) throw new Error('A Decode segment move has no stone.');
  return {
    move_type: 'rotate_segment' as const,
    segment_id: move.segmentId,
    direction: move.direction,
    client_elapsed_ms: move.clientElapsedMs,
  };
}
